import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Fade,
  Grow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CodeIcon from '@mui/icons-material/Code';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCourses } from '../store/thunks/coursesThunks';
import HomeSlider from '../components/HomeSlider';
import QuizSection from '../components/home/QuizSection';
import BlogSection from '../components/home/BlogSection';
import StatisticsSection from '../components/home/StatisticsSection';
import BannerSection from '../components/home/BannerSection';
import SEO from '../components/SEO';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { courses } = useAppSelector((state) => state.courses);

  useEffect(() => {
    if (courses.length === 0) {
      dispatch(fetchCourses());
    }
  }, [dispatch, courses.length]);

  // Show featured courses (first 6, or available courses)
  const featuredCourses = courses.slice(0, 6).length > 0 ? courses.slice(0, 6) : [];

  const features = [
    {
      icon: <CodeIcon sx={{ fontSize: 48 }} />,
      title: 'Interactive Learning',
      description: 'Hands-on coding exercises and real-world projects that prepare you for industry challenges',
      color: 'primary',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
      title: 'Track Progress',
      description: 'Monitor your learning journey with detailed analytics and personalized insights',
      color: 'success',
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 48 }} />,
      title: 'Expert Community',
      description: 'Learn from experienced developers and connect with peers in our growing community',
      color: 'secondary',
    },
  ];

  const siteUrl = 'https://learn2skill.com';
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'Learn2Skill',
    description: 'Master programming skills with comprehensive courses, interactive quizzes, and expert-led content',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    sameAs: [
      'https://twitter.com/learn2skill',
      'https://facebook.com/learn2skill',
      'https://linkedin.com/company/learn2skill',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'support@learn2skill.com',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '1250',
    },
  };

  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Learn2Skill',
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SEO
        title="Learn2Skill - Master Programming Skills with Interactive Courses & Quizzes"
        description="Learn programming with comprehensive courses in JavaScript, TypeScript, Python, C#, Java, React, Vue, Angular, and more. Interactive quizzes, expert-led tutorials, hands-on projects, and real-world applications. Start your coding journey today!"
        keywords="programming courses, coding tutorials, learn JavaScript, learn Python, learn React, web development, software engineering, coding bootcamp, online learning, programming education, interactive quizzes, coding practice, developer training"
        url="/"
        type="website"
        structuredData={[structuredData, websiteStructuredData]}
      />
      {/* Full-Width Hero Section with Slider */}
      <Box sx={{ position: 'relative', mb: 0 }}>
        <HomeSlider />
      </Box>

      {/* Banner Section */}
      <BannerSection />

      {/* Features Section - Enhanced Design */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(15, 23, 42, 1) 100%)',
          py: { xs: 8, md: 12 },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.5) 50%, transparent 100%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Why Choose Our Platform?
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: '700px', mx: 'auto', fontWeight: 400 }}
              >
                Experience world-class learning with cutting-edge features designed to accelerate your growth
              </Typography>
            </Box>
          </Fade>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Grow in timeout={1000 + index * 200}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 4,
                      background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                      border: '1px solid rgba(148, 163, 184, 0.1)',
                      borderRadius: 4,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${
                          feature.color === 'primary'
                            ? '#6366f1'
                            : feature.color === 'success'
                            ? '#10b981'
                            : '#ec4899'
                        } 0%, transparent 100%)`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      },
                      '&:hover': {
                        transform: 'translateY(-12px)',
                        borderColor: `rgba(${
                          feature.color === 'primary'
                            ? '99, 102, 241'
                            : feature.color === 'success'
                            ? '16, 185, 129'
                            : '236, 72, 153'
                        }, 0.4)`,
                        boxShadow: `0 24px 48px rgba(${
                          feature.color === 'primary'
                            ? '99, 102, 241'
                            : feature.color === 'success'
                            ? '16, 185, 129'
                            : '236, 72, 153'
                        }, 0.25)`,
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, rgba(${
                          feature.color === 'primary'
                            ? '99, 102, 241'
                            : feature.color === 'success'
                            ? '16, 185, 129'
                            : '236, 72, 153'
                        }, 0.2) 0%, rgba(${
                          feature.color === 'primary'
                            ? '139, 92, 246'
                            : feature.color === 'success'
                            ? '52, 211, 153'
                            : '251, 113, 133'
                        }, 0.2) 100%)`,
                        mb: 3,
                        color: `${feature.color}.main`,
                        border: `1px solid rgba(${
                          feature.color === 'primary'
                            ? '99, 102, 241'
                            : feature.color === 'success'
                            ? '16, 185, 129'
                            : '236, 72, 153'
                        }, 0.3)`,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                      {feature.description}
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured Courses Section - Enhanced */}
      {featuredCourses.length > 0 && (
        <Box
          sx={{
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 1) 0%, rgba(0, 0, 0, 1) 100%)',
            py: { xs: 8, md: 12 },
            position: 'relative',
          }}
        >
          <Container maxWidth="lg">
            <Fade in timeout={1000}>
              <Box sx={{ textAlign: 'center', mb: 8 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    px: 3,
                    py: 1,
                    borderRadius: 3,
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                  }}
                >
                  <StarIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'primary.light',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Featured Courses
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    fontSize: { xs: '2rem', md: '3rem' },
                    background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Start Your Learning Journey
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto', fontWeight: 400 }}>
                  Explore our most popular courses designed by industry experts
                </Typography>
              </Box>
            </Fade>
            <Grid container spacing={4}>
              {featuredCourses.map((course, index) => (
                <Grid item xs={12} sm={6} md={4} key={course.id}>
                  <Grow in timeout={1200 + index * 150}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%)',
                        border: '1px solid rgba(148, 163, 184, 0.1)',
                        borderRadius: 4,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-16px) scale(1.02)',
                          borderColor: 'rgba(99, 102, 241, 0.5)',
                          boxShadow: '0 32px 64px rgba(99, 102, 241, 0.3)',
                          '& .course-image': {
                            transform: 'scale(1.1)',
                          },
                        },
                      }}
                      onClick={() => navigate(`/courses/${course.id}`)}
                    >
                      <Box
                        className="course-image"
                        sx={{
                          height: 220,
                          background: `linear-gradient(135deg, rgba(99, 102, 241, 0.4) 0%, rgba(236, 72, 153, 0.4) 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)',
                          },
                        }}
                      >
                        <SchoolIcon
                          sx={{
                            fontSize: 90,
                            color: 'primary.main',
                            position: 'relative',
                            zIndex: 1,
                            filter: 'drop-shadow(0 4px 12px rgba(99, 102, 241, 0.5))',
                          }}
                        />
                      </Box>
                      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Chip
                            label={course.category}
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                              border: '1px solid rgba(99, 102, 241, 0.3)',
                              fontWeight: 600,
                              color: 'primary.light',
                            }}
                          />
                          {course.averageRating && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                              <Typography variant="caption" color="text.secondary">
                                {course.averageRating.toFixed(1)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          gutterBottom
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '3.5rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {course.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 3,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            flexGrow: 1,
                            lineHeight: 1.6,
                          }}
                        >
                          {course.description}
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.4)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(99, 102, 241, 0.5)',
                            },
                          }}
                        >
                          Start Learning
                        </Button>
                      </CardContent>
                    </Card>
                  </Grow>
                </Grid>
              ))}
            </Grid>
            {featuredCourses.length > 0 && (
              <Box sx={{ textAlign: 'center', mt: 8 }}>
                <Button
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/courses')}
                  sx={{
                    px: 5,
                    py: 2,
                    borderRadius: 3,
                    borderWidth: 2,
                    borderColor: 'rgba(99, 102, 241, 0.5)',
                    color: 'primary.light',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: '#6366f1',
                      borderWidth: 2,
                      background: 'rgba(99, 102, 241, 0.1)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(99, 102, 241, 0.3)',
                    },
                  }}
                >
                  View All Courses
                </Button>
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* Blog Section */}
      <BlogSection />

      {/* Quiz Section */}
      <QuizSection />

      {/* Statistics Section */}
      <StatisticsSection />
    </Box>
  );
};

export default HomePage;
